'use client';

import { useGetUserQuery } from '@api/api';
import { userState } from '../redux/userSlice/userSlice';
import Modal from './Modal';
import { useSearchParams } from 'next/navigation';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import {
  customerState,
  customerTypes,
} from '../redux/customerSlice/customerSlice';
import { useCustomerSetter } from '../redux/customerSlice/customerHandlers';
import { MdLogout, MdPerson } from 'react-icons/md';
import { BiSolidFactory } from 'react-icons/bi';
import { useLogoutHandler } from '../redux/userSlice/loginHandlers';
import Link from 'next-intl/link';
import { useTranslations } from 'next-intl';
import { useLocaleRoutes } from '../utils/useLocaleRoutes';
import useParamsEditor from '../utils/useParamsEditor';
import QueryLink from './QueryLink';
import { AuthenticationContent } from './AuthenticationModal';
import { useState } from 'react';

export const Account = () => {
  const t = useTranslations('customer');
  const f = useTranslations('FirstLoginModal');
  const n = useTranslations('Navbar');
  const u = useTranslations('user');
  const path = useLocaleRoutes();
  const editParam = useParamsEditor();
  const [authenticate, setAuthenticate] = useState(false);
  const user: userState = useSelector((state: RootState) => {
    return state.user;
  });
  const customer: customerState = useSelector((state: RootState) => {
    return state.customer;
  });
  const { data: fetchedUser } = useGetUserQuery(user.userId, {
    skip: !user.loggedIn,
  });
  const logout = useLogoutHandler();
  const setCustomer = useCustomerSetter();
  const setCompany = (company: any) => {
    setCustomer({
      id: company.id,
      type: customerTypes.company,
      name: company.name,
    });
    editParam('modal');
  };
  const setPrivateCustomer = (customerId: number) => {
    setCustomer({
      id: customerId,
      type: customerTypes.private,
      name: `${user.firstName} ${user.lastName}`,
    });
    editParam('modal');
  };
  const logoutHandler = () => {
    logout();
    editParam('modal');
  };
  const userData = fetchedUser?.data;
  const companiesCount = userData?.company_customers?.length ?? 0;
  const privateCount = userData?.private_customer ? 1 : 0;
  const total = companiesCount + privateCount;

  const footer = (
    <div className='mt-3 grid grid-cols-2 gap-3 text-center'>
      <Link
        href={`/${path('profile')}`}
        className='rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
      >
        {f('goToProfile')}
      </Link>
      <button
        onClick={logoutHandler}
        className='flex items-center justify-center gap-1 rounded-sm border border-brand bg-brand px-4 py-2 font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
      >
        <MdLogout /> {n('logout')}
      </button>
    </div>
  );

  if (authenticate) {
    return (
        <AuthenticationContent />
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      {fetchedUser && !total && (
        <p className='text-center'>{t('noCustomers')}</p>
      )}
      {!!total && customer.id && (
        <div className='mb-4'>
          <p className='mb-2 w-full text-center'>{t('biddingAs')}</p>
          <p className='flex flex-wrap items-center justify-center gap-2 text-xl font-semibold'>
            {customer.type === customerTypes.company ? (
              <BiSolidFactory />
            ) : (
              <MdPerson />
            )}
            {customer.name}
          </p>
        </div>
      )}
      <div className='flex flex-col'>
        {total > 1 && <p className='mb-2'>{t('otherCustomers')}:</p>}
        {userData?.company_customers?.map((customerItem: any) => {
          if (
            customer.id === customerItem.id &&
            customer.type === customerTypes.company
          )
            return null;
          return (
            <button
              className='flex items-center gap-2 border-t py-2 last:border-b hover:text-brand focus:text-brand'
              key={customerItem.id}
              onClick={() => {
                setCompany(customerItem);
              }}
            >
              <BiSolidFactory />
              {customerItem.name}
            </button>
          );
        })}
        {userData?.private_customer &&
          ((customer.id !== userData?.private_customer.id &&
            customer.type === customerTypes.private) ||
            customer.type !== customerTypes.private) && (
            <button
              className='flex items-center gap-2 border-t py-2 last:border-b hover:text-brand focus:text-brand'
              onClick={() => {
                setPrivateCustomer(userData.private_customer.id);
              }}
            >
              <MdPerson />
              {user.firstName} {user.lastName}
            </button>
          )}
      </div>
      {userData && !userData.identity_verified_at && (
        <>
          <button
            onClick={() => setAuthenticate(true)}
            className='mt-6 w-full rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
          >
            {u('registerCompanyNow')}
          </button>
        </>
      )}
      {userData && !!userData.identity_verified_at && (
        <QueryLink
          query={{ modal: 'register-company' }}
          className='mt-6 w-full rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
        >
          {u('registerCompanyNow')}
        </QueryLink>
      )}
      {footer}
    </div>
  );
};

export default function AccountModal() {
  const searchParams = useSearchParams();
  const showAccountForm = searchParams.get('modal') === 'account';
  return (
    <>
      {showAccountForm && (
        <Modal>
          <Account />
        </Modal>
      )}
    </>
  );
}
